/**
 * useApiData.js
 * Fetches data from the Express/MySQL backend.
 * Falls back to MOCK_DATA if the API is unreachable.
 */
import { useState, useEffect } from "react";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// ── Fallback mock data (used only when backend is down) ──────────────────────
const MOCK_DATA = {
  notices: [
    { id: 1, title: "Advertisement for the post of Medical Officer (Contractual) at ART Centre", date: "2026-02-16", link: "#", isNew: true },
    { id: 2, title: "Advertisement for filling up of the post of Office Assistant SMTI under NHM", date: "2026-01-20", link: "#", isNew: true },
    { id: 3, title: "Notice for Reporting for Admission into BMLT/BMRT/DMLT/DMRT/COA/COSA/COTT Course", date: "2026-01-08", link: "#", isNew: false },
    { id: 4, title: "INFORMATION FOR PG (MD/MS) ADMISSION 2025", date: "2025-12-15", link: "#", isNew: false },
    { id: 5, title: "Recruitment Advertisement for different posts of NPAC MKCG MCB", date: "2025-11-20", link: "#", isNew: false },
    { id: 6, title: "Notice for the white coat ceremony held on 10-11-2025 for 1st Year MBBS Students", date: "2025-11-10", link: "#", isNew: false },
  ],
  results: [
    { id: 1, title: "Result of 2nd Prof. MBBS of Supple Exam January 2026", date: "2026-02-01", link: "#" },
    { id: 2, title: "Result of 3rd Prof. (Part-I) MBBS of Exam of December 2025", date: "2026-01-15", link: "#" },
    { id: 3, title: "Result of 2nd Prof. MBBS of Exam of September, 2025 - 2023 Batch", date: "2025-10-01", link: "#" },
    { id: 4, title: "Result of PG MD MS Examination of August, 2025", date: "2025-09-15", link: "#" },
    { id: 5, title: "Result of 1st Prof. MBBS of Exam of August, 2025 - 2024 Batch", date: "2025-09-01", link: "#" },
    { id: 6, title: "Result of 2nd Prof. MBBS of Suppl. Exam of August, 2025", date: "2025-08-20", link: "#" },
  ],
  admissions: [
    { id: 1, title: "INFORMATION FOR MBBS ADMISSION 2025", date: "2025-08-01", link: "#", isNew: true },
    { id: 2, title: "INFORMATION FOR DM/M.Ch. ADMISSION-2025", date: "2025-07-15", link: "#", isNew: false },
    { id: 3, title: "INFORMATION FOR PG (MD/MS) ADMISSION 2024", date: "2024-12-01", link: "#", isNew: false },
    { id: 4, title: "INFORMATION FOR MBBS ADMISSION 2024", date: "2024-11-01", link: "#", isNew: true },
  ],
  tenders: [
    { id: 1, title: "Tender for Supply of Consumable, Disposable & other items, for CTVS", date: "2026-04-15", link: "#", isNew: true },
    { id: 2, title: "Tender Call Notice for Supply of PRP Machine for Pain Clinic, MKCG MCH", date: "2026-04-10", link: "#", isNew: true },
    { id: 3, title: "Tender call notice for supply for fire extinguisher & filling up", date: "2026-03-20", link: "#", isNew: false },
    { id: 4, title: "Publication of Auction Sale of Condemned and scrap materials", date: "2026-03-01", link: "#", isNew: false },
  ],
  stats: [
    { label: "MBBS Students", value: "250+",  icon: "🎓" },
    { label: "PG Students",   value: "1000+", icon: "🏥" },
    { label: "Departments",   value: "28",    icon: "🔬" },
    { label: "Acres Campus",  value: "162",   icon: "🌳" },
  ],
 
};

// ── Hook ─────────────────────────────────────────────────────────────────────
export function useApiData(endpoint) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [source,  setSource]  = useState(null); // "api" | "mock"

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 5000); // 5 s timeout

        const res = await fetch(`${BASE_URL}/${endpoint}`, {
          signal: controller.signal,
        });
        clearTimeout(timer);

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setSource("api");
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(`[useApiData] /${endpoint} – falling back to mock:`, err.message);
          setData(MOCK_DATA[endpoint] ?? []);
          setSource("mock");
          setError(err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [endpoint]);

  return { data, loading, error, source };
}

export { MOCK_DATA };
